import React, { useEffect, useState, useCallback, useRef } from "react";
import { CollectionLoader } from "../../loader/Loader";
import moment from "moment";
import PostRequest from "../../services/PostRequest";
import { McaEmpty, McaPagination, PAGE_SIZE } from "../../components/mca";
import {
  SOURCE_LABEL, SEVERITY_LABEL, SEVERITY_CLASS,
  STATUS_LABEL, STATUS_CLASS, dateFromForRange,
} from "../../data/errorLogConstants";

// Server-side paginated/filtered error table.
// `filters` = { source, severity, status, range, q }. `reloadKey` forces a refetch
// (e.g. after a status change in the drawer).
const ErrorTable = ({ filters, reloadKey, onErrorClick }) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const load = useCallback(
    async (pg) => {
      setLoader(true);
      try {
        const res = await PostRequest("/users/error/logs/", {
          with_count: true,
          skip: (pg - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
          source: filters.source,
          severity: filters.severity,
          status: filters.status,
          date_from: dateFromForRange(filters.range),
          search: filters.q,
        });
        setRows(Array.isArray(res?.results) ? res.results : []);
        setTotal(res?.total_count || 0);
      } catch (e) {
        console.log(e.message);
        setRows([]);
        setTotal(0);
      } finally {
        setLoader(false);
      }
    },
    [filters.source, filters.severity, filters.status, filters.range, filters.q]
  );

  // One fetch per change. When a filter/search changes we reset to page 1; if we
  // were past page 1 the setPage triggers the (single) fetch, so we return early
  // here to avoid a wasted fetch at the stale offset.
  const filterSig = JSON.stringify([
    filters.source, filters.severity, filters.status, filters.range, filters.q,
  ]);
  const sigRef = useRef(filterSig);
  useEffect(() => {
    if (sigRef.current !== filterSig) {
      sigRef.current = filterSig;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    load(page);
    // load is intentionally omitted — it's recreated each render with the latest
    // filters, and filterSig already captures every input that changes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterSig, reloadKey]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const sev = (s) => [SEVERITY_CLASS[s] || "soft-muted", SEVERITY_LABEL[s] || s || "—"];

  if (loader && rows.length === 0) {
    return (
      <div className="mca-table-wrap">
        <table className="mca-table">
          <tbody>
            <CollectionLoader />
          </tbody>
        </table>
      </div>
    );
  }

  if (!loader && rows.length === 0) {
    return (
      <div className="mca-table-wrap">
        <McaEmpty
          title="No errors found"
          description="API-submission and scrape failures will appear here."
          minHeight={true}
        />
      </div>
    );
  }

  return (
    <div className="mca-table-wrap">
      <table className="mca-table">
        <thead>
          <tr>
            <th>Error</th>
            <th>Source</th>
            <th>Severity</th>
            <th>Status</th>
            <th className="hide-md">ASIN</th>
            <th className="hide-md">Collection</th>
            <th className="hide-md">Time</th>
            <th style={{ textAlign: "right" }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => {
            const [cls, label] = sev(e.severity);
            return (
              <tr
                key={e.id}
                className="clickable"
                onClick={() => onErrorClick(e)}
                style={{ cursor: "pointer" }}
              >
                <td className="cell-strong">{e.error_title || e.reason || "—"}</td>
                <td>
                  <span className="mca-badge mca-badge--soft-muted">
                    {SOURCE_LABEL[e.source] || e.source || "—"}
                  </span>
                </td>
                <td>
                  <span className={`mca-badge mca-badge--${cls}`}>
                    <span className="dot"></span>
                    {label}
                  </span>
                </td>
                <td>
                  <span className={`mca-badge mca-badge--${STATUS_CLASS[e.status] || "soft-muted"}`}>
                    {STATUS_LABEL[e.status] || e.status || "New"}
                  </span>
                </td>
                <td className="hide-md mca-cell-mono">{e.asin_code || "—"}</td>
                <td className="hide-md cell-muted">{e.collection_name || "—"}</td>
                <td className="hide-md cell-muted" style={{ fontSize: "13px" }}>
                  {e.created_at ? moment(e.created_at).fromNow() : "—"}
                </td>
                <td style={{ textAlign: "right" }}>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="var(--mca-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 6 6 6-6 6" />
                  </svg>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="table-foot">
        <span>
          {total === 0
            ? "No errors"
            : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total} errors`}
        </span>
        <McaPagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>
    </div>
  );
};

export default ErrorTable;
